import type { ConversationListRow as ConversationResponse } from './conversation-event-preview';

type ConversationPage = {
  items: ConversationResponse[];
  [key: string]: unknown;
};

type InfiniteConversationCache = {
  pages: ConversationPage[];
  pageParams: unknown[];
  [key: string]: unknown;
};

/**
 * Applies one list transform to either conversation-query cache shape:
 * the legacy flat snapshot or the paginated Inbox infinite query.
 */
export function transformConversationCache(
  cache: unknown,
  transform: (items: ConversationResponse[]) => ConversationResponse[],
): unknown {
  if (Array.isArray(cache)) {
    return transform(cache as ConversationResponse[]);
  }
  if (
    cache &&
    typeof cache === 'object' &&
    Array.isArray((cache as { pages?: unknown }).pages)
  ) {
    const infinite = cache as InfiniteConversationCache;
    return {
      ...infinite,
      pages: infinite.pages.map((page) => ({
        ...page,
        items: transform(Array.isArray(page.items) ? page.items : []),
      })),
    };
  }
  return cache;
}

/** True only when the conversation is already present in a visible list cache. */
export function conversationCacheContains(cache: unknown, conversationId: string): boolean {
  if (!conversationId) return false;
  if (Array.isArray(cache)) {
    return (cache as ConversationResponse[]).some((conversation) => conversation.id === conversationId);
  }
  if (
    cache &&
    typeof cache === 'object' &&
    Array.isArray((cache as { pages?: unknown }).pages)
  ) {
    return (cache as InfiniteConversationCache).pages.some(
      (page) =>
        Array.isArray(page.items) &&
        page.items.some((conversation) => conversation.id === conversationId),
    );
  }
  return false;
}
